<template>
  <div class="coro-widget">
    <div
      v-if="widgetConfig.metadata.state === WidgetState.SETUP_REQUIRED"
      class="coro-widget__content coro-widget--empty"
      data-testid="device-widget-no-devices-container"
    >
      <v-icon class="empty-data-icon" :icon="`$${CoroIcons.DEVICE_XS}`" />
      <div class="subtitle1 text-center">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.noDevicesProtected`) }}
      </div>
      <div class="caption text-indigo-medium mb-4">
        {{ $t(`dashboard.widgets.${widgetConfig.type}.installCoroClients`) }}
      </div>
      <v-btn
        v-if="canManageDevicesSettings"
        :to="{ name: RouteName.DEVICES_SETTINGS_PAGE }"
        color="primary"
        rounded
        data-testid="device-widget-add-protection-btn"
      >
        {{ $t(`dashboard.widgets.${widgetConfig.type}.addProtectionBtn`) }}
      </v-btn>
    </div>

    <div v-else-if="widgetDataValue.data" class="coro-widget__content">
      <div class="coro-widget__tile coro-widget__tile-content">
        <div class="d-inline-flex">
          <div class="d-flex align-start flex-column">
            <div class="coro-widget-number" data-testid="device-widget-protected-devices">
              {{ widgetDataValue.data.protectedDevicesCount }}
            </div>
            <div class="body2">
              <span>{{ $t(`dashboard.widgets.${widgetConfig.type}.protectedDevices`) }}</span>
              <span
                v-if="!isAccessRestricted(RolePermissionsScope.VIEWS, 'devicesView')"
                @click="goToDevices()"
              >
                <a class="coro-link text-no-wrap ml-2" data-testid="devices-widget-view-devices"
                  >{{ $t("general.view") }} ›</a
                >
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="coro-widget__tile device-table">
        <vulnerabilities-tile
          data-testid="device-widget-vulnerabilities-table"
          :widget-type="widgetConfig.type"
          :widget-data="widgetDataValue.data"
          @all-active-link-clicked="viewAllTickets(true, TicketsModuleFilter.DEVICE_WIDGET)"
          @all-caught-up-link-clicked="viewAllTickets(false, TicketsModuleFilter.DEVICE_WIDGET)"
          @vulnerability-clicked="viewVulnerability($event, TicketsModuleFilter.DEVICE_WIDGET)"
        ></vulnerabilities-tile>
        <generated-tickets-trend-chart
          v-if="widgetConfig.metadata.state === WidgetState.HAS_OPEN_TICKETS"
          class="pt-2 px-6"
          :chart-data="widgetDataValue.data.generatedTicketsHistory"
          :delta="widgetDataValue.data.generatedTicketsDelta"
        ></generated-tickets-trend-chart>
      </div>
    </div>

    <widget-error-state
      v-else
      :widget-type="widgetConfig.type"
      @retry-click="$emit('fetch-data')"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import { TicketsModuleFilter } from "@/constants/tickets";
import VulnerabilitiesTile from "@/components/VulnerabilitiesTile.vue";
import { useRouter } from "vue-router";
import kebabCase from "lodash/kebabCase";
import { useWidgetContent } from "@/composables/useWidgetContent";
import { CoroIcons } from "@/constants/coro-icon";
import { RouteName } from "@/constants/routes";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import GeneratedTicketsTrendChart from "@/components/dashboard/GeneratedTicketsTrendChart.vue";
import type { WidgetType } from "@/constants/dashboard.ts";
import WidgetErrorState from "@/components/dashboard/WidgetErrorState.vue";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<WidgetConfiguration<WidgetType.ENDPOINT_SECURITY>>,
      required: true,
    },
  },
  components: {
    WidgetErrorState,
    GeneratedTicketsTrendChart,
    VulnerabilitiesTile,
  },
  setup(props) {
    const router = useRouter();
    const { viewVulnerability, viewAllTickets, isAccessRestricted } = useWidgetContent();

    const widgetDataValue = computed(() => props.widgetConfig.data);

    const canManageDevicesSettings = computed(
      () =>
        !isAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.DEVICES
        )
    );

    const goToDevices = async () => {
      await router.push({ name: RouteName.DEVICES });
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${startCase(props.widgetConfig.type)} - ${GTMParams.VIEW_DEVICES}`
      );
    };

    return {
      canManageDevicesSettings,
      CoroIcons,
      widgetDataValue,
      TicketsModuleFilter,
      viewVulnerability,
      viewAllTickets,
      isAccessRestricted,
      RolePermissionsScope,
      goToDevices,
      kebabCase,
      RouteName,
      WidgetState,
    };
  },
});
</script>
